"use client";

import React, { useEffect, useRef, useState } from "react";
import {
  Bell,
  Building2,
  CalendarDays,
  CheckCheck,
  ChevronRight,
  DollarSign,
  Download,
  Home,
  LogIn,
  LogOut,
  Mail,
  MapPin,
  Pencil,
  Radio,
  RefreshCw,
  ShieldCheck,
  Target,
  Trash2,
  Trophy,
  UserRound,
  Users,
  Wifi,
  WifiOff,
} from "lucide-react";
import { auth } from "./auth";
import { memberApi } from "./memberApi";
import { offlineStore } from "./offlineDb";
import { setContext, syncQueuedOperations } from "./api";
import { useInstallPrompt } from "./useInstallPrompt";
import { applyPlatformIdentity, platformIdentity } from "./platformIdentity";
import { matchLiveCopy, scoreboardLaunchUrl, scoreboardStatusLabel } from "./scoreboard";
import {
  assignLineupPlayer,
  availableLineupPlayers,
  lineupPlayerOptionLabel,
  lineupPlayerSectionRecord,
} from "./lineup";
import {
  applyScoresheetGameChange,
  createScoresheetState,
  chicagoDiscipline,
  hydrateScoresheet,
  isChicagoScoresheetGame,
  isCricketScoresheetGame,
  matchScoresheetTotals,
  reviewScoresheet,
  scoresheetGameDisplayName,
  shouldApplyScoresheetRefresh,
} from "./scoresheet";
import {
  claimTokenFromLocation,
  routeFromLocation,
  teamInviteTokenFromLocation,
  teamJoinIdFromLocation,
  teamRegistrationIdFromLocation,
  tournamentIdFromLocation,
} from "./lib/routing";
import {
  dateLabel,
  money,
  ordinal,
  rankLabel,
  tournamentDateLabel,
  tournamentStatusCopy,
  usPhoneDigits,
  usPhoneForApi,
} from "./lib/format";
import {
  appVersion,
  appVersionParts,
  fullAppVersion,
  refreshApplication,
  scoreboardBaseUrl,
  scoresheetV2Enabled,
  urlBase64ToUint8Array,
} from "./lib/appRuntime";
import {
  Brand,
  BrowserInstallRecommendation,
  EmptyState,
  LeagueIdentityHeader,
  NavButton,
  PerspectiveSwitch,
  PullToRefresh,
} from "./components/shared";
import { demoDashboard } from "./lib/demoData";
import { CricketScore, LiveScorer, X01Score, gameDefinitions } from "./screens/live";
import {
  ClaimScreen,
  OrganizationPicker,
  SmsOptInDemo,
  TeamInvitationScreen,
  TeamJoinScreen,
  TeamRegistrationScreen,
  Welcome,
} from "./screens/onboarding";
import { TournamentRegistrationScreen, TournamentRegistrationsView } from "./screens/tournaments";
import { NotificationsView, ProfileView, ScheduleView, SwipeNotification } from "./screens/player";
import { InviteNeeded, MembershipRequired } from "./screens/access";
import {
  CaptainLineup,
  MatchPrepView,
  MatchScoresheetEntry,
  ScoresheetGameEditor,
  ScoresheetReview,
  SeasonLeaderboardModal,
} from "./screens/match";
import { CaptainWorkspace } from "./screens/captain";

function Shell({
  account,
  organizationName,
  online,
  onSwitchOrganization,
  canSwitchOrganization,
  install,
}) {
  const [section, setSection] = useState("home");
  const [perspective, setPerspective] = useState("player");
  const [dashboard, setDashboard] = useState(null);
  const [notice, setNotice] = useState("");
  const [membershipMissing, setMembershipMissing] = useState(false);
  const [captainTeams, setCaptainTeams] = useState([]);
  const [captainLoadError, setCaptainLoadError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [tournamentRegistrations, setTournamentRegistrations] = useState([]);
  const [tournamentsLoading, setTournamentsLoading] = useState(true);
  const [seasonLeaderboard, setSeasonLeaderboard] = useState(null);
  useEffect(() => {
    (async () => {
      const organizationId = await offlineStore.getPreference("organizationId");
      const cacheKey = `member-dashboard:v2:${organizationId || "demo"}`;
      const cached = await offlineStore.getPreference(cacheKey);
      if (cached) setDashboard(cached);
      let ownedTeams = [];
      if (auth.configured)
        try {
          ownedTeams = await memberApi.captainTeams();
        } catch (error) {
          if ((error.status || 0) >= 500)
            setCaptainLoadError(
              "Team Captain tools could not load. Try again in a moment.",
            );
        }
      setCaptainTeams(ownedTeams);
      try {
        const value = auth.configured
          ? await memberApi.dashboard()
          : demoDashboard;
        setDashboard(value);
        await offlineStore.setPreference(cacheKey, value);
      } catch (error) {
        if (error.status === 404) {
          setDashboard(null);
          setMembershipMissing(ownedTeams.length === 0);
          if (ownedTeams.length) setPerspective("team");
        } else if (!cached) setNotice(error.message);
      } finally {
        setLoaded(true);
      }
    })();
  }, []);
  useEffect(() => {
    if (!auth.configured || !online) return;
    const refresh = () =>
      memberApi
        .notifications(true)
        .then((value) => setUnreadNotifications(value.unreadCount))
        .catch(() => {});
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => clearInterval(timer);
  }, [online]);
  useEffect(() => {
    if (!auth.configured) {
      setTournamentsLoading(false);
      return;
    }
    memberApi
      .tournamentRegistrations()
      .then(setTournamentRegistrations)
      .catch(() => {})
      .finally(() => setTournamentsLoading(false));
  }, []);
  if (perspective === "team" && captainTeams.length)
    return (
      <>
        <BrowserInstallRecommendation install={install} />
        <CaptainWorkspace
          teams={captainTeams}
          organizationName={organizationName || dashboard?.organizationName}
          online={online}
          onPlayerView={() => {
            setPerspective("player");
            setSection("home");
          }}
          onSwitch={onSwitchOrganization}
          canSwitchOrganization={canSwitchOrganization}
        />
      </>
    );
  if (membershipMissing)
    return (
      <>
        <BrowserInstallRecommendation install={install} />
        <MembershipRequired onSwitch={onSwitchOrganization} />
      </>
    );
  if (!dashboard)
    return (
      <>
        <BrowserInstallRecommendation install={install} />
        <div className="launch-screen">
          <span className="target-mark">◎</span>
          <b>
            {loaded
              ? notice || "YOUR PLAYER PROFILE IS NOT CONNECTED"
              : notice || "LOADING YOUR DARTS"}
          </b>
          {captainTeams.length > 0 && (
            <button
              className="action-primary"
              onClick={() => setPerspective("team")}
            >
              Open Team Dashboard
            </button>
          )}
        </div>
      </>
    );
  const nextMatch =
    dashboard.matches.find((match) => !match.isBye) || dashboard.matches[0];
  const seasonSnapshot = nextMatch?.seasonSnapshot;
  const primaryTeam = dashboard.teams[0];
  const pendingRegistrations = captainTeams.reduce(
    (sum, team) => sum + (team.pendingRegistrationCount || 0),
    0,
  );
  return (
    <>
      <BrowserInstallRecommendation install={install} />
      <div className="mobile-shell">
        <LeagueIdentityHeader
          organizationName={dashboard.organizationName || organizationName}
          leagueName={nextMatch?.leagueName || dashboard.leagueName || primaryTeam?.leagueName}
          online={online}
          onSwitchOrganization={onSwitchOrganization}
          canSwitchOrganization={canSwitchOrganization}
        />
        {captainTeams.length > 0 && (
          <PerspectiveSwitch
            perspective="player"
            captainTeams={captainTeams}
            onChange={setPerspective}
          />
        )}
        <main
          className={`app-content ${captainTeams.length > 0 ? "with-perspective" : ""}`}
        >
          {section === "home" && (
            <section className="home-view">
              <div className="greeting">
                <span>PLAYER DASHBOARD</span>
                <h1>
                  {dashboard.profile.firstName} {dashboard.profile.lastName}
                </h1>
                <p>
                  {primaryTeam
                    ? `${primaryTeam.teamName} · ${primaryTeam.seasonName} · ${primaryTeam.nightName}`
                    : "Your match-night home is ready."}
                </p>
              </div>
              {captainLoadError && (
                <button
                  className="captain-load-error"
                  onClick={() => location.reload()}
                >
                  <span>
                    <b>Captain tools unavailable</b>
                    <small>{captainLoadError}</small>
                  </span>
                  <span>Retry</span>
                </button>
              )}
              {pendingRegistrations > 0 && (
                <button
                  className="captain-alert"
                  onClick={() => setPerspective("team")}
                >
                  <div>
                    <span className="kicker">CAPTAIN TASK</span>
                    <b>
                      Finish{" "}
                      {pendingRegistrations === 1
                        ? "team registration"
                        : `${pendingRegistrations} team registrations`}
                    </b>
                    <small>
                      Add the remaining players and submit your roster.
                    </small>
                  </div>
                  <ChevronRight />
                </button>
              )}
              {tournamentRegistrations.some(
                (value) => !["Withdrawn", "Rejected"].includes(value.status),
              ) && (
                <button
                  className="tournament-alert"
                  onClick={() => setSection("tournaments")}
                >
                  <Trophy />
                  <span>
                    <small>TOURNAMENTS</small>
                    <b>
                      {
                        tournamentRegistrations.filter(
                          (value) =>
                            !["Withdrawn", "Rejected"].includes(value.status),
                        ).length
                      }{" "}
                      upcoming registration
                      {tournamentRegistrations.filter(
                        (value) =>
                          !["Withdrawn", "Rejected"].includes(value.status),
                      ).length === 1
                        ? ""
                        : "s"}
                    </b>
                  </span>
                  <ChevronRight />
                </button>
              )}
              <article className="hero-match">
                <button className="hero-match-main" onClick={() => setSection("match")}>
                  <span className="kicker">NEXT MATCH</span>
                  {nextMatch && (
                    <div className="hero-match-context">
                      <strong>{nextMatch.leagueName}</strong>
                      <small>{[nextMatch.seasonName, nextMatch.divisionName, nextMatch.nightName].filter(Boolean).join(" · ")}</small>
                      <small>Playing for {nextMatch.teamName}</small>
                    </div>
                  )}
                  <h2>
                    {nextMatch
                      ? nextMatch.isBye
                        ? "Bye week"
                        : `${nextMatch.isHome ? "vs" : "at"} ${nextMatch.opponentName}`
                      : "Schedule coming soon"}
                  </h2>
                  <p>
                    {nextMatch
                      ? `${dateLabel(nextMatch.matchDate)} · ${nextMatch.pubName || "Venue to be announced"}`
                      : "Your league organizer has not published the next round yet."}
                  </p>
                  <span className="hero-link">
                    Open match night <ChevronRight size={18} />
                  </span>
                </button>
                {seasonSnapshot && (
                  <div className="season-snapshot" aria-label="My season snapshot">
                    <span className="season-snapshot-title">MY SEASON</span>
                    <div className="season-snapshot-grid">
                      <span><small>Matches</small><b>{seasonSnapshot.playerMatchesPlayed}</b></span>
                      <span><small>Games</small><b>{seasonSnapshot.playerGamesPlayed}</b></span>
                      <span><small>Team record</small><b>{seasonSnapshot.teamWins}–{seasonSnapshot.teamLosses}{seasonSnapshot.teamTies ? `–${seasonSnapshot.teamTies}` : ""}</b><em>{seasonSnapshot.teamWinPercentage}%</em></span>
                      <span><small>Standing</small><b>{ordinal(seasonSnapshot.teamStanding)}</b><em>of {seasonSnapshot.teamCount}</em></span>
                      <button
                        aria-label="View X01 quality point placement"
                        className="season-snapshot-placement"
                        type="button"
                        onClick={() => setSeasonLeaderboard({
                          title: "X01 Quality Points",
                          context: [nextMatch?.seasonName, nextMatch?.nightName].filter(Boolean).join(" · "),
                          entries: seasonSnapshot.qualityPointLeaders || [],
                        })}
                      >
                        <small>X01 QPs</small><b>{seasonSnapshot.qualityPoints}</b>
                        <em>{rankLabel(seasonSnapshot.qualityPointRank, seasonSnapshot.rankedPlayerCount)}</em>
                        <i>View placement <ChevronRight size={11} /></i>
                      </button>
                      <button
                        aria-label="View Cricket marks placement"
                        className="season-snapshot-placement"
                        type="button"
                        onClick={() => setSeasonLeaderboard({
                          title: "Cricket Marks",
                          context: [nextMatch?.seasonName, nextMatch?.nightName].filter(Boolean).join(" · "),
                          entries: seasonSnapshot.cricketMarkLeaders || [],
                        })}
                      >
                        <small>Cricket marks</small><b>{seasonSnapshot.cricketMarks}</b>
                        <em>{rankLabel(seasonSnapshot.cricketMarksRank, seasonSnapshot.rankedPlayerCount)}</em>
                        <i>View placement <ChevronRight size={11} /></i>
                      </button>
                    </div>
                  </div>
                )}
              </article>
              <SeasonLeaderboardModal value={seasonLeaderboard} onClose={() => setSeasonLeaderboard(null)} />
              <a className="private-match-home" href={scoreboardBaseUrl}>
                <Target />
                <span>
                  <b>Score a private match</b>
                  <small>X01 or Cricket · No league schedule required</small>
                </span>
                <ChevronRight />
              </a>
              <section className="quick-grid">
                <button onClick={() => setSection("schedule")}>
                  <CalendarDays />
                  <b>Schedule</b>
                  <span>{dashboard.matches.length} upcoming</span>
                </button>
                <button onClick={() => setSection("tournaments")}>
                  <Trophy />
                  <b>Tournaments</b>
                  <span>
                    {tournamentRegistrations.length
                      ? `${tournamentRegistrations.length} registrations`
                      : "Your events"}
                  </span>
                </button>
              </section>
            </section>
          )}
          {section === "match" && (
            <MatchPrepView
              match={nextMatch}
              teams={dashboard.teams}
              online={online}
              showCurrentMatchStatus
              playerContext
            />
          )}
          {section === "schedule" && (
            <ScheduleView matches={dashboard.matches} />
          )}
          {section === "tournaments" && (
            <TournamentRegistrationsView
              registrations={tournamentRegistrations}
              loading={tournamentsLoading}
              onOpen={(publicId) => {
                history.pushState({}, "", `/tournaments/${publicId}`);
                location.reload();
              }}
            />
          )}
          {section === "notifications" && (
            <NotificationsView onUnreadChanged={setUnreadNotifications} />
          )}
          {section === "profile" && (
            <ProfileView
              dashboard={dashboard}
              onSwitch={onSwitchOrganization}
              onSaved={async (profile) => {
                const saved = auth.configured
                  ? await memberApi.updateProfile(profile)
                  : profile;
                const updated = { ...dashboard, profile: saved };
                const organizationId =
                  await offlineStore.getPreference("organizationId");
                setDashboard(updated);
                await offlineStore.setPreference(
                  `member-dashboard:${organizationId || "demo"}`,
                  updated,
                );
                return saved;
              }}
            />
          )}
        </main>
        <nav className="bottom-nav" aria-label="Primary navigation">
          <NavButton
            active={section === "home"}
            onClick={() => setSection("home")}
            icon={<Home />}
            label="Home"
          />
          <NavButton
            active={section === "schedule"}
            onClick={() => setSection("schedule")}
            icon={<CalendarDays />}
            label="Schedule"
          />
          <NavButton
            prominent
            active={section === "match"}
            onClick={() => setSection("match")}
            icon={<Radio />}
            label="Match"
          />
          <NavButton
            active={section === "notifications"}
            onClick={() => setSection("notifications")}
            icon={
              <span className="notification-nav-icon">
                <Bell />
                {unreadNotifications > 0 && (
                  <i>{unreadNotifications > 9 ? "9+" : unreadNotifications}</i>
                )}
              </span>
            }
            label="Updates"
          />
          <NavButton
            active={section === "profile"}
            onClick={() => setSection("profile")}
            icon={<UserRound />}
            label="Me"
          />
        </nav>
      </div>
    </>
  );
}

function AuthenticatedMobileApp() {
  const [ready, setReady] = useState(false);
  const [entered, setEntered] = useState(false);
  const [account, setAccount] = useState(null);
  const [online, setOnline] = useState(globalThis.navigator?.onLine !== false);
  const [organizations, setOrganizations] = useState(null);
  const [memberContext, setMemberContext] = useState(null);
  const install = useInstallPrompt();

  useEffect(() => {
    const start = async () => {
      if ("serviceWorker" in navigator && import.meta.env.PROD)
        await navigator.serviceWorker.register("/service-worker.js", { updateViaCache: "none" });
      try {
        applyPlatformIdentity(await memberApi.platformIdentity());
      } catch {
        applyPlatformIdentity();
      }
      setAccount(await auth.currentAccount());
      setEntered(
        Boolean(await offlineStore.getPreference("entered")) ||
          Boolean(claimTokenFromLocation()) ||
          Boolean(teamInviteTokenFromLocation()) ||
          Boolean(teamJoinIdFromLocation()) ||
          Boolean(teamRegistrationIdFromLocation()) ||
          Boolean(tournamentIdFromLocation()),
      );
      setReady(true);
    };
    start();
    const wentOnline = () => {
      setOnline(true);
      syncQueuedOperations().catch(() => {});
    };
    const wentOffline = () => setOnline(false);
    addEventListener("online", wentOnline);
    addEventListener("offline", wentOffline);
    return () => {
      removeEventListener("online", wentOnline);
      removeEventListener("offline", wentOffline);
    };
  }, []);

  useEffect(() => {
    if (
      !ready ||
      !account ||
      !auth.configured ||
      claimTokenFromLocation() ||
      teamInviteTokenFromLocation() ||
      teamJoinIdFromLocation() ||
      teamRegistrationIdFromLocation() ||
      tournamentIdFromLocation()
    )
      return;
    memberApi
      .organizations()
      .then(setOrganizations)
      .catch((error) => {
        if (error.status === 404) setOrganizations([]);
      });
  }, [ready, account]);

  useEffect(() => {
    if (organizations?.length === 1 && !memberContext)
      memberApi.selectOrganization(organizations[0]).then(setMemberContext);
  }, [organizations, memberContext]);

  if (!ready)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>OCHE</b>
      </div>
    );
  const claimToken = claimTokenFromLocation();
  if (claimToken)
    return (
      <ClaimScreen
        token={claimToken}
        account={account}
        onClaimed={(context) => {
          setMemberContext(context);
          history.replaceState({}, "", "/");
          setEntered(true);
        }}
      />
    );
  const teamInviteToken = teamInviteTokenFromLocation();
  if (teamInviteToken)
    return <TeamInvitationScreen token={teamInviteToken} account={account} />;
  const teamJoinId = teamJoinIdFromLocation();
  if (teamJoinId)
    return <TeamJoinScreen publicId={teamJoinId} account={account} />;
  const teamRegistrationId = teamRegistrationIdFromLocation();
  if (teamRegistrationId)
    return (
      <TeamRegistrationScreen publicId={teamRegistrationId} account={account} />
    );
  const tournamentId = tournamentIdFromLocation();
  if (tournamentId)
    return (
      <TournamentRegistrationScreen publicId={tournamentId} account={account} />
    );
  if (!entered)
    return (
      <Welcome
        install={install}
        onContinue={async () => {
          await offlineStore.setPreference("entered", true);
          if (!account) await auth.signIn("/");
          else setEntered(true);
        }}
      />
    );
  if (auth.configured && !account)
    return <Welcome install={install} onContinue={() => auth.signIn("/")} />;
  if (auth.configured && organizations?.length === 0 && !memberContext)
    return <InviteNeeded account={account} online={online} install={install} />;
  if (auth.configured && organizations?.length > 1 && !memberContext)
    return (
      <>
        <BrowserInstallRecommendation install={install} />
        <OrganizationPicker
          organizations={organizations}
          onSelect={async (organization) =>
            setMemberContext(await memberApi.selectOrganization(organization))
          }
        />
      </>
    );
  if (auth.configured && organizations?.length === 1 && !memberContext)
    return (
      <div className="launch-screen">
        <span className="target-mark">◎</span>
        <b>LOADING YOUR LEAGUE</b>
      </div>
    );
  return (
    <Shell
      account={memberContext?.account || account}
      organizationName={memberContext?.organizationName}
      online={online}
      install={install}
      canSwitchOrganization={(organizations?.length || 0) > 1}
      onSwitchOrganization={async () => {
        await offlineStore.removePreference("member-dashboard");
        setMemberContext(null);
        setOrganizations(await memberApi.organizations());
      }}
    />
  );
}

export default function MobileApp() {
  if (/^\/sms-opt-in\/?$/i.test(routeFromLocation())) return <SmsOptInDemo />;
  return (
    <>
      <PullToRefresh />
      <AuthenticatedMobileApp />
    </>
  );
}
